import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Key, Users, CreditCard, Webhook, Bell, Shield, Plus, Trash2, Copy,
  CheckCircle, Settings, Lock, Globe, Mail, Smartphone, Eye, EyeOff,
  AlertTriangle, ExternalLink, Monitor, Download,
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
import APIKeysTab from "@/components/APIKeysTab";
import TeamManagementTab from "@/components/TeamManagementTab";
import BillingTab from "@/components/BillingTab";
import SecuritySettingsTab from "@/components/SecuritySettingsTab";

const WEBHOOKS = [
  { id: "wh_1a2b3c", url: "https://api.acme.com/webhooks/qnneal", events: ["optimization.completed", "cost.threshold"], status: "active", lastTriggered: "2026-02-14T09:23:11Z", successRate: 99.8 },
  { id: "wh_4d5e6f", url: "https://hooks.slack.com/services/T00/B00/xxxx", events: ["provider.outage", "billing.invoice"], status: "active", lastTriggered: "2026-02-14T08:45:33Z", successRate: 100 },
  { id: "wh_7g8h9i", url: "https://pagerduty.com/integrate/events/qnneal", events: ["provider.outage", "swarm.failure"], status: "paused", lastTriggered: "2026-02-13T22:10:05Z", successRate: 97.2 },
];

const NOTIFICATION_PREFS = [
  { id: "cost_alert", label: "Cost Threshold Alerts", desc: "When monthly spend exceeds configured limits", email: true, slack: true, push: false },
  { id: "provider_outage", label: "Provider Outage", desc: "When a provider experiences downtime", email: true, slack: true, push: true },
  { id: "optimization_report", label: "Daily Optimization Report", desc: "Summary of routing decisions and savings", email: true, slack: false, push: false },
  { id: "swarm_complete", label: "Swarm Execution Complete", desc: "When an agent swarm finishes execution", email: false, slack: true, push: true },
  { id: "security_alert", label: "Security Alerts", desc: "Suspicious login attempts and API key usage", email: true, slack: true, push: true },
  { id: "billing_invoice", label: "Billing & Invoices", desc: "New invoices and payment confirmations", email: true, slack: false, push: false },
  { id: "model_update", label: "Model Updates", desc: "When new models are added or deprecated", email: false, slack: true, push: false },
];

const WEBHOOK_EVENTS = [
  "optimization.completed", "cost.threshold", "provider.outage", "billing.invoice",
  "swarm.started", "swarm.failure", "swarm.completed", "api_key.created", "model.deprecated",
];

function DemoModeTab() {
  const [demoMode, setDemoMode] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/dashboard/demo-mode`, { credentials: "include" })
      .then(r => r.json())
      .then(d => { setDemoMode(d.demo_mode || false); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const toggleDemo = async () => {
    const newVal = !demoMode;
    try {
      const res = await fetch(`${API}/api/dashboard/demo-mode`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ enabled: newVal }),
      });
      if (res.ok) { setDemoMode(newVal); toast.success(`Demo mode ${newVal ? "enabled" : "disabled"}`); }
    } catch { toast.error("Failed to toggle demo mode"); }
  };

  return (
    <div className="space-y-4" data-testid="demo-mode-section">
      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
        <CardHeader>
          <CardTitle className="text-base font-outfit flex items-center gap-2">
            <Monitor className="w-4 h-4 text-[var(--qn-primary)]" /> Demo Mode
          </CardTitle>
          <CardDescription className="text-xs text-[var(--qn-foreground-muted)]">
            When enabled, dashboards display curated demo data for investor presentations and product demos.
            Your real data remains unchanged and private.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 rounded-lg border border-[var(--qn-border)] bg-[var(--qn-surface)]/30">
            <div>
              <p className="text-sm font-medium text-[var(--qn-foreground)]">Enable Demo Mode</p>
              <p className="text-xs text-[var(--qn-foreground-muted)]">
                {demoMode ? "Showing curated demo data on dashboards" : "Showing your real usage data"}
              </p>
            </div>
            <Switch checked={demoMode} onCheckedChange={toggleDemo} disabled={loading} data-testid="demo-mode-toggle" />
          </div>
          {demoMode && (
            <div className="p-3 rounded-lg border border-amber-500/20 bg-amber-500/5 text-xs text-amber-400 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>Demo mode is active. Dashboard metrics show curated data, not your actual usage. Disable before making business decisions.</span>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ExportTab() {
  const handleExport = async (endpoint, filename) => {
    try {
      const res = await fetch(`${API}/api/dashboard/export/${endpoint}`, { credentials: "include" });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${filename}`);
    } catch { toast.error("Export failed"); }
  };

  return (
    <div className="space-y-4" data-testid="export-section">
      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
        <CardHeader>
          <CardTitle className="text-base font-outfit flex items-center gap-2">
            <Download className="w-4 h-4 text-[var(--qn-primary)]" /> Data Export
          </CardTitle>
          <CardDescription className="text-xs text-[var(--qn-foreground-muted)]">
            Download your optimization and swarm execution data for compliance reporting and analysis.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {[
            { label: "Optimizations (CSV)", desc: "All optimization records with cost breakdowns", endpoint: "optimizations/csv", filename: "qnneal_optimizations.csv" },
            { label: "Swarm History (CSV)", desc: "All swarm execution records with token usage", endpoint: "swarm-history/csv", filename: "qnneal_swarm_history.csv" },
            { label: "Full Analytics (JSON)", desc: "Complete analytics summary for the last 30 days", endpoint: "analytics/json", filename: "qnneal_analytics.json" },
          ].map(item => (
            <div key={item.endpoint} className="flex items-center justify-between p-4 rounded-lg border border-[var(--qn-border)] bg-[var(--qn-surface)]/30">
              <div>
                <p className="text-sm font-medium text-[var(--qn-foreground)]">{item.label}</p>
                <p className="text-xs text-[var(--qn-foreground-muted)]">{item.desc}</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => handleExport(item.endpoint, item.filename)} data-testid={`export-${item.endpoint.replace(/\//g, "-")}`}>
                <Download className="w-3.5 h-3.5 mr-2" /> Download
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

export default function SettingsPage() {
  const [activeSettingsTab, setActiveSettingsTab] = useState("api-keys");
  const [webhooks, setWebhooks] = useState(WEBHOOKS);
  const [notifications, setNotifications] = useState(NOTIFICATION_PREFS);
  const [addWebhookOpen, setAddWebhookOpen] = useState(false);
  const [newWebhook, setNewWebhook] = useState({ url: "", events: [] });

  const toggleNotification = (id, channel) => {
    setNotifications((prev) =>
      prev.map((n) => n.id === id ? { ...n, [channel]: !n[channel] } : n)
    );
    toast.success("Notification preference updated");
  };

  const deleteWebhook = (id) => {
    setWebhooks((prev) => prev.filter((w) => w.id !== id));
    toast.success("Webhook deleted");
  };

  const toggleWebhookStatus = (id) => {
    setWebhooks((prev) =>
      prev.map((w) => w.id === id ? { ...w, status: w.status === "active" ? "paused" : "active" } : w)
    );
  };

  const addWebhook = () => {
    if (!newWebhook.url) return;
    const wh = {
      id: `wh_${Math.random().toString(36).slice(2, 8)}`,
      url: newWebhook.url,
      events: newWebhook.events,
      status: "active",
      lastTriggered: null,
      successRate: 100,
    };
    setWebhooks((prev) => [...prev, wh]);
    setNewWebhook({ url: "", events: [] });
    setAddWebhookOpen(false);
    toast.success("Webhook created");
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-outfit font-bold text-[var(--qn-foreground)]">Settings & Configuration</h1>
        <p className="text-sm text-[var(--qn-foreground-muted)] mt-1">Manage API keys, team, billing, webhooks, notifications, and security</p>
      </div>

      <Tabs value={activeSettingsTab} onValueChange={setActiveSettingsTab}>
        <TabsList className="bg-[var(--qn-surface)] border border-[var(--qn-border)] flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="api-keys" data-testid="settings-tab-api-keys"><Key className="w-4 h-4 mr-2" />API Keys</TabsTrigger>
          <TabsTrigger value="team" data-testid="settings-tab-team"><Users className="w-4 h-4 mr-2" />Team</TabsTrigger>
          <TabsTrigger value="billing" data-testid="settings-tab-billing"><CreditCard className="w-4 h-4 mr-2" />Billing</TabsTrigger>
          <TabsTrigger value="webhooks" data-testid="settings-tab-webhooks"><Webhook className="w-4 h-4 mr-2" />Webhooks</TabsTrigger>
          <TabsTrigger value="notifications" data-testid="settings-tab-notifications"><Bell className="w-4 h-4 mr-2" />Notifications</TabsTrigger>
          <TabsTrigger value="security" data-testid="settings-tab-security"><Shield className="w-4 h-4 mr-2" />Security</TabsTrigger>
          <TabsTrigger value="demo" data-testid="settings-tab-demo"><Monitor className="w-4 h-4 mr-2" />Demo Mode</TabsTrigger>
          <TabsTrigger value="export" data-testid="settings-tab-export"><Download className="w-4 h-4 mr-2" />Export</TabsTrigger>
        </TabsList>

        <TabsContent value="api-keys"><APIKeysTab /></TabsContent>
        <TabsContent value="team"><TeamManagementTab /></TabsContent>
        <TabsContent value="billing"><BillingTab /></TabsContent>

        {/* Webhooks */}
        <TabsContent value="webhooks">
          <div className="space-y-4" data-testid="webhooks-section">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-base font-semibold text-[var(--qn-foreground)]">Webhook Endpoints</h2>
                <p className="text-xs text-[var(--qn-foreground-muted)]">Receive real-time event notifications via HTTP POST</p>
              </div>
              <Button size="sm" onClick={() => setAddWebhookOpen(true)} data-testid="add-webhook-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Webhook
              </Button>
            </div>

            {webhooks.map((wh, idx) => (
              <motion.div
                key={wh.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
              >
                <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]" data-testid={`webhook-${wh.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <Webhook className="w-4 h-4 text-[var(--qn-primary)]" />
                        <code className="text-xs font-mono text-[var(--qn-foreground)] bg-[var(--qn-surface)] px-2 py-1 rounded">{wh.url}</code>
                        <Badge variant={wh.status === "active" ? "default" : "secondary"} className="text-[10px]">
                          {wh.status}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => toggleWebhookStatus(wh.id)} className="text-xs">
                          {wh.status === "active" ? "Pause" : "Resume"}
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => deleteWebhook(wh.id)} className="text-red-400 hover:text-red-500 h-8 w-8">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-[var(--qn-foreground-muted)]">
                      <span>Events: {wh.events.map((e) => <Badge key={e} variant="outline" className="text-[9px] mr-1">{e}</Badge>)}</span>
                    </div>
                    <div className="flex items-center gap-6 mt-2 text-[11px] text-[var(--qn-foreground-subtle)]">
                      <span>Success: <span className="font-mono text-emerald-500">{wh.successRate}%</span></span>
                      {wh.lastTriggered && <span>Last: {new Date(wh.lastTriggered).toLocaleString()}</span>}
                      <span className="font-mono text-[var(--qn-foreground-muted)]">{wh.id}</span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Add Webhook Dialog */}
          <Dialog open={addWebhookOpen} onOpenChange={setAddWebhookOpen}>
            <DialogContent className="bg-[var(--qn-card)] border-[var(--qn-border)]" data-testid="add-webhook-modal">
              <DialogHeader>
                <DialogTitle className="font-outfit">Add Webhook Endpoint</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div>
                  <label className="text-xs text-[var(--qn-foreground-muted)] mb-1 block">Endpoint URL</label>
                  <Input
                    placeholder="https://your-app.com/webhooks/qnneal"
                    value={newWebhook.url}
                    onChange={(e) => setNewWebhook({ ...newWebhook, url: e.target.value })}
                    className="bg-[var(--qn-surface)] border-[var(--qn-border)]"
                    data-testid="webhook-url-input"
                  />
                </div>
                <div>
                  <label className="text-xs text-[var(--qn-foreground-muted)] mb-2 block">Events to Subscribe</label>
                  <div className="flex flex-wrap gap-2">
                    {WEBHOOK_EVENTS.map((evt) => {
                      const selected = newWebhook.events.includes(evt);
                      return (
                        <Badge
                          key={evt}
                          variant={selected ? "default" : "outline"}
                          className={`cursor-pointer text-[10px] ${selected ? "bg-[var(--qn-primary)] text-white" : ""}`}
                          onClick={() => setNewWebhook({
                            ...newWebhook,
                            events: selected ? newWebhook.events.filter((e) => e !== evt) : [...newWebhook.events, evt],
                          })}
                        >
                          {evt}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setAddWebhookOpen(false)}>Cancel</Button>
                <Button onClick={addWebhook} data-testid="webhook-submit-btn">Create Webhook</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Notifications */}
        <TabsContent value="notifications">
          <div className="space-y-4" data-testid="notifications-section">
            <div>
              <h2 className="text-base font-semibold text-[var(--qn-foreground)]">Notification Preferences</h2>
              <p className="text-xs text-[var(--qn-foreground-muted)]">Choose how you want to be notified for each event type</p>
            </div>

            <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
              <CardContent className="p-0">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[var(--qn-border)] bg-[var(--qn-surface)]/50">
                      <th className="text-left py-3 px-4 text-[var(--qn-foreground-muted)] font-medium text-xs">Notification</th>
                      <th className="text-center py-3 px-4 text-[var(--qn-foreground-muted)] font-medium text-xs w-20">
                        <Mail className="w-4 h-4 mx-auto" />
                      </th>
                      <th className="text-center py-3 px-4 text-[var(--qn-foreground-muted)] font-medium text-xs w-20">
                        <Globe className="w-4 h-4 mx-auto" />
                      </th>
                      <th className="text-center py-3 px-4 text-[var(--qn-foreground-muted)] font-medium text-xs w-20">
                        <Smartphone className="w-4 h-4 mx-auto" />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {notifications.map((n) => (
                      <tr key={n.id} className="border-b border-[var(--qn-border)]/30 hover:bg-[var(--qn-surface)]/20" data-testid={`notification-${n.id}`}>
                        <td className="py-3 px-4">
                          <div className="text-sm font-medium text-[var(--qn-foreground)]">{n.label}</div>
                          <div className="text-[11px] text-[var(--qn-foreground-muted)]">{n.desc}</div>
                        </td>
                        <td className="text-center py-3 px-4">
                          <Switch checked={n.email} onCheckedChange={() => toggleNotification(n.id, "email")} />
                        </td>
                        <td className="text-center py-3 px-4">
                          <Switch checked={n.slack} onCheckedChange={() => toggleNotification(n.id, "slack")} />
                        </td>
                        <td className="text-center py-3 px-4">
                          <Switch checked={n.push} onCheckedChange={() => toggleNotification(n.id, "push")} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Security */}
        <TabsContent value="security">
          <SecuritySettingsTab />
        </TabsContent>

        {/* Demo Mode */}
        <TabsContent value="demo">
          <DemoModeTab />
        </TabsContent>

        {/* Export */}
        <TabsContent value="export">
          <ExportTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
